import { ApolloCache, DefaultContext, MutationUpdaterFunction, useMutation } from '@apollo/client';
import { Modal } from '@ui-components/neo.tax';
import { ReactElement } from 'react';

import { DeleteProductMutationVariables, DeleteProductMutation, DELETE_PRODUCT, Exact } from '@neo.tax/shared/graphql';
import { Product } from '@neo.tax/shared/hooks/useProducts';

import { DeleteConfirmationLayout } from '../delete-confirmation-layout';

type DeleteProductModalProps = {
  product: Product | null;
  onClose: () => void;
  onDelete: MutationUpdaterFunction<
    DeleteProductMutation,
    Exact<{
      productId: string;
    }>,
    DefaultContext,
    ApolloCache<any>
  >;
};

export const DeleteProductModal = ({ onClose, onDelete, product }: DeleteProductModalProps): ReactElement => {
  const [deleteProduct, { loading }] = useMutation<DeleteProductMutation, DeleteProductMutationVariables>(
    DELETE_PRODUCT,
  );

  const handleConfirmDelete = async (): Promise<void> => {
    if (!product) return;
    await deleteProduct({
      variables: { productId: product.id },
      optimisticResponse: {
        deleteProduct: {
          id: product.id,
          __typename: 'Product',
        },
      },
      update: (cache, { data }, context) => {
        onDelete(cache, { data }, context);
        cache.evict({ id: cache.identify(product) });
      },
    });
    onClose();
  };

  return (
    <Modal isOpen={!!product} setIsOpen={onClose}>
      {product && (
        <DeleteConfirmationLayout
          title={`Delete "${product.name}"`}
          description="Would you like to delete this product? This action cannot be undone."
          close={onClose}
          deleteMutation={handleConfirmDelete}
          loading={loading}
        />
      )}
    </Modal>
  );
};
